/**
 * Change which settlement document the Net Wire Calculator balances against
 * (funding-statements.md § "Change the source", 2026-10-05).
 *
 *   GET  /funding_statements/{dealId}/hud_sources → { documents, source,
 *        warnings, uploadDocTypes }
 *   POST /funding_statements/{dealId}/hud_source  { documentId } | { documentId: null }
 *        → { netWireCalculator, extractionQueued }   (409 once locked)
 *
 * Rows that cannot balance the borrower side (a seller-only ALTA, a net-wire
 * worksheet) are shown but disabled. "Automatic" sends `documentId: null`.
 * Uploading a statement goes through the normal loan-docs upload with one of
 * the `uploadDocTypes`, then the list is re-read.
 *
 * Built on the form-guide primitives (FORM_STYLE_GUIDE.md): a pinned header,
 * selectable FormChoiceCards for the documents, FormFooter for the actions,
 * and previews that open over the dialog.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import CloseRounded from "@mui/icons-material/CloseRounded";
import UploadFileOutlined from "@mui/icons-material/UploadFileOutlined";

import { formatDateTime } from "../../../../../_src_shared/utils/functions/formatDateTime";
import {
  FormChoiceCard,
  FormFooter,
  FormNote,
  FormOptionChips,
  FormPill,
  FormSection,
  FormWarn,
} from "../../../../../components/forms/FormPrimitives";
import Flx from "../../../../../components/layout/Flx";
import Txt from "../../../../../components/typography/Txt";
import { useFundingStatementsHook } from "../../../../../hooks/useFundingStatementsHook";
import { useLoanFilesHook } from "../../../../../hooks/useLoanFilesHook";
import { usePreviewOverDialog } from "../../../../../hooks/usePreviewOverDialog";
import { useUnderwritingHook } from "../../../../../hooks/useUnderwritingHook";
import { loanDrilldownSet } from "../../../../../redux/actions/loanDrilldownActions";
import { filePreviewSetData } from "../../../../../redux/reducers/filePreviewReducer";
import { FORM, formSx, px } from "../../../../../theme/formStyles";
import { money } from "../wireFundingShared";
import { settlementTypeLabel, useResolveLoanDocument } from "./HudSourceChip";

const AUTO = "__auto__";
const stamp = () => new Date().toISOString().replace(/[:.]/g, "-");

/** One figure in a row's quick preview; hidden when the extract lacks it. */
const Fig = ({ label, value }) =>
  value == null ? null : (
    <Box component="span" sx={{ whiteSpace: "nowrap" }}>
      {label} <b style={{ fontVariantNumeric: "tabular-nums" }}>{money(value)}</b>
    </Box>
  );

const HudSourcePicker = ({ open, onClose, dealId, locked, onChosen }) => {
  const dispatch = useDispatch();
  const { getHudSources, setHudSource } = useFundingStatementsHook();
  const { uploadLoanDocs } = useLoanFilesHook();
  const { getLoanById } = useUnderwritingHook();
  const { dialogProps } = usePreviewOverDialog(onClose);
  const resolveDoc = useResolveLoanDocument();
  const groupsAndTypes = useSelector(
    (s) => s.fetchedSelections?.fileDocGroupAndTypes,
  );

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [choice, setChoice] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadType, setUploadType] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    if (!dealId) return;
    setLoading(true);
    setFailed(false);
    getHudSources({
      dealId,
      onSuccessFn: (res) => {
        setData(res || null);
        const current = (res?.documents || []).find((d) => d.isNetWireSource);
        // The current pick is preselected so "Use this statement" only lights
        // up on a real change.
        setChoice(
          res?.source?.selection === "selected" && current
            ? current.documentId
            : AUTO,
        );
        setUploadType((t) => t || res?.uploadDocTypes?.[0] || "");
      },
      onFailFn: () => setFailed(true),
      onCompleteFn: () => setLoading(false),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const documents = useMemo(() => data?.documents || [], [data]);
  const initialChoice = useMemo(() => {
    const current = documents.find((d) => d.isNetWireSource);
    return data?.source?.selection === "selected" && current
      ? current.documentId
      : AUTO;
  }, [data, documents]);

  const preview = (e, doc) => {
    e.stopPropagation();
    const full = resolveDoc(doc);
    if (full) dispatch(filePreviewSetData(full));
  };

  const handleConfirm = () => {
    setSaving(true);
    setHudSource({
      dealId,
      payload: { documentId: choice === AUTO ? null : choice },
      onSuccessMsg:
        choice === AUTO
          ? "HUD source set to automatic"
          : "HUD source changed",
      onSuccessFn: (res) => {
        onChosen?.(res);
        onClose();
      },
      onCompleteFn: () => setSaving(false),
    });
  };

  const docGroupFor = (docType) => {
    if (!groupsAndTypes || typeof groupsAndTypes !== "object") return undefined;
    const hit = Object.entries(groupsAndTypes).find(
      ([, types]) => Array.isArray(types) && types.includes(docType),
    );
    return hit ? hit[0] : undefined;
  };

  const onPickFiles = async (e) => {
    const list = Array.from(e.target.files || []);
    e.target.value = "";
    if (!list.length || !dealId || !uploadType) return;
    const docGroup = docGroupFor(uploadType);
    const files = list.map((file) => ({
      file,
      id: `${file.name.replace(/\.[^.]+$/, "")}-${stamp()}${file.name.match(/\.[^.]+$/)?.[0] || ""}`,
      name: file.name,
      ...(docGroup ? { docGroup } : {}),
      docType: uploadType,
    }));
    setUploading(true);
    await uploadLoanDocs({
      loanId: dealId,
      files,
      onSuccessMsg: `${list.length} settlement statement${list.length === 1 ? "" : "s"} uploaded`,
      onSuccessFn: () => {
        setUploading(false);
        // The new file has to be on the loan for its preview to resolve, and
        // in the list before it can be picked.
        getLoanById({
          loanId: dealId,
          onSuccessFn: (loan) => dispatch(loanDrilldownSet(loan)),
        });
        load();
      },
      onFailFn: () => setUploading(false),
    });
  };

  const changed = choice != null && choice !== initialChoice;

  return (
    <Dialog
      open={open}
      {...dialogProps}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: formSx.paper }}
    >
      <Box sx={formSx.head}>
        <Flx jb g={2} sx={{ alignItems: "flex-start" }}>
          <Box sx={{ minWidth: 0 }}>
            <Txt sx={formSx.title}>Change the HUD source</Txt>
            <Txt sx={formSx.titleHelper}>
              The settlement statement the Net Wire Calculator balances against.
            </Txt>
          </Box>
          <IconButton
            size="small"
            onClick={onClose}
            aria-label="Close"
            sx={{ mt: px(-4), mr: px(-8) }}
          >
            <CloseRounded sx={{ fontSize: 18 }} />
          </IconButton>
        </Flx>
      </Box>

      <DialogContent sx={formSx.shellBody}>
        {locked ? (
          <Box sx={{ mb: px(FORM.s.s5) }}>
            <FormWarn>
              <b>The funding statement is locked.</b> The locked statement keeps
              the HUD it funded on. Unlock it to change the source.
            </FormWarn>
          </Box>
        ) : null}

        {loading && !data ? (
          <Flx center sx={{ py: 5 }}>
            <CircularProgress size={22} />
          </Flx>
        ) : failed ? (
          <FormNote>
            The settlement statements could not be loaded. This is available once
            the backend's HUD-source update is deployed.
          </FormNote>
        ) : (
          <>
            <FormSection
              n={1}
              title="Settlement statement"
              helper="Automatic picks the best match: can balance the borrower side, parsed, executed, Final HUD, not preliminary, newest."
            >
              <Flx column g={1}>
                <FormChoiceCard
                  selected={choice === AUTO}
                  disabled={locked}
                  onClick={() => setChoice(AUTO)}
                >
                  <Flx jb ac g={1.5} wrap>
                    <Txt sx={formSx.body}>Automatic</Txt>
                    {initialChoice === AUTO ? (
                      <FormPill tone="info">In use</FormPill>
                    ) : null}
                  </Flx>
                  <Txt sx={{ ...formSx.meta, display: "block", mt: px(2) }}>
                    The settlement workbook's document if there is one, otherwise
                    the best automatic match.
                  </Txt>
                </FormChoiceCard>

                {documents.length ? (
                  documents.map((d) => {
                    const blocked = d.canBalanceBorrowerSide === false;
                    const type = settlementTypeLabel(d);
                    const canPreview = !!resolveDoc(d);
                    return (
                      <FormChoiceCard
                        key={d.documentId}
                        selected={choice === d.documentId}
                        disabled={locked || blocked}
                        onClick={() => setChoice(d.documentId)}
                      >
                        <Flx jb g={1.5} sx={{ alignItems: "flex-start" }}>
                          <Box sx={{ minWidth: 0 }}>
                            <Link
                              component="button"
                              type="button"
                              disabled={!canPreview}
                              onClick={(e) => preview(e, d)}
                              sx={{
                                ...formSx.body,
                                fontWeight: 500,
                                textAlign: "left",
                                wordBreak: "break-word",
                                ...(canPreview ? {} : { color: FORM.color.ink2 }),
                              }}
                            >
                              {d.fileName || d.fileStorageName || "Settlement statement"}
                            </Link>
                            <Txt sx={{ ...formSx.meta, display: "block", mt: px(2) }}>
                              {[
                                d.uploadDate
                                  ? `Uploaded ${formatDateTime(d.uploadDate, "date")}`
                                  : null,
                                d.docType,
                              ]
                                .filter(Boolean)
                                .join(" · ")}
                            </Txt>
                          </Box>
                          <Flx ac g={0.75} wrap sx={{ justifyContent: "flex-end" }}>
                            {d.isNetWireSource ? (
                              <FormPill tone="info">In use</FormPill>
                            ) : null}
                            {type ? <FormPill>{type}</FormPill> : null}
                            {d.isExecuted === true ? (
                              <FormPill tone="success">Executed</FormPill>
                            ) : d.isExecuted === false ? (
                              <FormPill tone="warning">Draft</FormPill>
                            ) : null}
                            {d.parsed === false ? (
                              <FormPill tone="warning">Not read yet</FormPill>
                            ) : null}
                          </Flx>
                        </Flx>
                        {blocked ? (
                          <Txt sx={{ ...formSx.meta, display: "block", mt: px(6) }}>
                            Seller statement: can't balance the borrower side
                          </Txt>
                        ) : (
                          <Flx
                            g={2}
                            wrap
                            sx={{ ...formSx.meta, mt: px(6) }}
                          >
                            <Fig label="Cash from borrower" value={d.cashFromBorrower} />
                            <Fig label="Cash to borrower" value={d.cashToBorrower} />
                            <Fig label="Borrower debits (120)" value={d.borrowerTotalDebits} />
                            <Fig label="Borrower credits (220)" value={d.borrowerTotalCredits} />
                          </Flx>
                        )}
                      </FormChoiceCard>
                    );
                  })
                ) : (
                  <FormNote>
                    No HUD, ALTA or Closing Disclosure is on this loan yet.
                  </FormNote>
                )}
              </Flx>
              {choice !== AUTO &&
              documents.find((d) => d.documentId === choice)?.parsed === false ? (
                <Box sx={{ mt: px(FORM.s.s3) }}>
                  <FormNote>
                    This statement has not been read yet. Choosing it sends it for
                    extraction; until that finishes the balance uses lines prepared
                    from loan fields and has no variance.
                  </FormNote>
                </Box>
              ) : null}
            </FormSection>

            <Box sx={{ mt: px(FORM.s.s7) }}>
              <FormSection
                n={2}
                title="Upload a settlement statement"
                helper="It is added to the loan's documents and read automatically; pick it above once it appears."
              >
                <Flx column g={1.5}>
                  <FormOptionChips
                    options={data?.uploadDocTypes || []}
                    value={uploadType}
                    onChange={(v) => setUploadType(v || "")}
                  />
                  <input
                    ref={fileRef}
                    type="file"
                    multiple
                    hidden
                    accept=".pdf,image/*"
                    onChange={onPickFiles}
                  />
                  <Box>
                    <Button
                      variant="outlined"
                      startIcon={
                        uploading ? (
                          <CircularProgress size={14} />
                        ) : (
                          <UploadFileOutlined />
                        )
                      }
                      disabled={!uploadType || uploading}
                      onClick={() => fileRef.current?.click()}
                      sx={formSx.button}
                    >
                      {uploading ? "Uploading…" : "Choose file"}
                    </Button>
                  </Box>
                </Flx>
              </FormSection>
            </Box>
          </>
        )}
      </DialogContent>

      <FormFooter
        status={
          changed
            ? "Choosing a statement also dismisses any “newer version” notice."
            : null
        }
      >
        <Button variant="text" color="gray" onClick={onClose} sx={formSx.button}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!changed || locked || saving || failed}
          onClick={handleConfirm}
          sx={formSx.button}
        >
          {saving ? "Saving…" : "Use this statement"}
        </Button>
      </FormFooter>
    </Dialog>
  );
};

HudSourcePicker.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  dealId: PropTypes.string,
  locked: PropTypes.bool,
  /** Called with the POST response: { netWireCalculator, extractionQueued }. */
  onChosen: PropTypes.func,
};

export default HudSourcePicker;
